import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';

import './ArtRequestsAdmin.scss';
import EditSimRequestDrawer from './EditSimRequestDrawer';
import CommentsDialog from './CommentsDialog';
import NewsIcon from '../../../src/assets/images/news.svg';
import { useSimArtAdminProvider } from '../../providers/SimArtAdminProvider';
import Loader from '../Loader/Loader';
import AcDialog from '../AcDialog/AcDialog';

interface Props {
  position?: any;
}

export default function ArtRequestsAdmin({ position }: Props) {
  const { t } = useTranslation();
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] = useState<any>(null);
  const [isCommentsDialogOpen, setIsCommentsDialogOpen] = useState(false);
  const [selectedComments, setSelectedComments] = useState<any[]>([]);
  const [selectedRowForComments, setSelectedRowForComments] =
    useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [errMsg, setErrMsg] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);

  const { artDatesData, getArtDatesData, updateArtDate } =
    useSimArtAdminProvider();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await getArtDatesData();
      } catch (error: any) {
        const errorMessage =
          error?.message ||
          'An unexpected error occurred while fetching ART data.';
        setErrMsg(errorMessage);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getArtDatesData]);

  const columns: GridColDef[] = [
    {
      field: 'year',
      headerName: 'Year',
      width: 80,
      sortable: false,
      renderCell: (params: any) => {
        return <p className="art-requests-admin-table__year">{params.value}</p>;
      }
    },
    {
      field: 'month',
      headerName: 'Month',
      sortable: false,
      width: 110
    },
    {
      field: 'daterange',
      headerName: 'Date Range',
      width: 130,
      sortable: false
    },
    {
      field: 'submissionClose',
      headerName: 'Submission Close',
      width: 120,
      sortable: false
    },
    {
      field: 'sendDates',
      headerName: 'Send Dates',
      width: 150,
      sortable: false
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (params: any) => {
        const status = params.value;

        if (status === 'READY') {
          return (
            <div className="art-requests-admin-table__ready">
              <p>READY</p>
            </div>
          );
        } else if (status === 'NOT READY') {
          return (
            <p className="art-requests-admin-table__notready">NOT READY</p>
          );
        } else if (status === 'OPEN') {
          return (
            <div className="art-requests-admin-table__open">
              <p>OPEN</p>
            </div>
          );
        } else if (status === 'CLOSED') {
          return (
            <div className="art-requests-admin-table__closed">
              <p>CLOSED</p>
            </div>
          );
        }

        return <p>{status}</p>;
      },
      sortable: false
    },
    {
      field: 'comments',
      headerName: 'Comments',
      flex: 1,
      renderCell: (params: any) => {
        const commentCount = params.value || 0;
        return (
          <div
            className="art-requests-admin-table__comments"
            onClick={() => handleCommentsClick(params.row)}
            style={{ cursor: 'pointer' }}
          >
            <img src={NewsIcon} alt="news-icon" />
            <p>{commentCount === 0 ? '' : `${commentCount} Comments`}</p>
          </div>
        );
      },
      sortable: false
    },
    {
      field: 'lastmodified',
      headerName: 'Last Modified',
      width: 200,
      sortable: false
    },
    {
      field: 'edit',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params) => {
        if (!artDatesData || artDatesData.length === 0) {
          return (
            <button
              className="art-requests-admin-table__edit-button"
              onClick={() => handleEdit(params.row)}
            >
              <i className="icon-edit black" />
              <span className="art-requests-admin-table__edit">Edit</span>
            </button>
          );
        }

        const hasOpenStatus = artDatesData.some(
          (row: any) => row.status === 'OPEN'
        );

        const hasReadyStatus = artDatesData.some(
          (row: any) => row.status === 'READY'
        );

        const closedRows =
          artDatesData?.filter((row: any) => row.status === 'CLOSED') || [];

        const sortedClosedRows = closedRows.sort((a, b) => {
          const yearA = a.year;
          const yearB = b.year;
          if (yearA !== yearB) return yearB - yearA;

          const monthNames = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
          ];
          const monthA = monthNames.indexOf(a.month);
          const monthB = monthNames.indexOf(b.month);
          return monthB - monthA;
        });

        const lastClosedRow = sortedClosedRows[0];
        const isLastClosedMonth =
          lastClosedRow && params.row.id === lastClosedRow.id;

        let isDisabled = false;

        if (params.row.status === 'CLOSED') {
          if (hasOpenStatus) {
            isDisabled = true;
          } else if (hasReadyStatus) {
            isDisabled = !isLastClosedMonth;
          }
        }

        return (
          <button
            className={`art-requests-admin-table__edit-button ${isDisabled ? 'disabled' : ''}`}
            onClick={() => !isDisabled && handleEdit(params.row)}
            disabled={isDisabled}
          >
            <i className="icon-edit black" />
            <span className="art-requests-admin-table__edit">Edit</span>
          </button>
        );
      }
    }
  ];

  const handleEdit = (row: any) => {
    const mappedRow = {
      ...row,
      simYear: row.year,
      simMonth: row.month,
      simStatus: row.status,
      simCloseDateTime: row.artCloseDateTime || row.submissionClose,
      simSendDate: row.artSendDate || row.sendDates,
      simOpenDateTime: row.artOpenDateTime || '',
      simCloseDate: row.artCloseDate || row.artCloseDateTime || row.sendDates
    };
    setSelectedRowForEdit(mappedRow);
    setIsEditDrawerOpen(true);
  };

  const handleSaveEdit = async (updatedRow: any) => {
    setLoading(true);
    try {
      const mappedUpdatedRow = {
        artRequestID: updatedRow.simRequestID || updatedRow.id,
        year: updatedRow.simYear,
        month: updatedRow.simMonth,
        artOpenDateTime: updatedRow.simOpenDateTime,
        artCloseDateTime: updatedRow.simCloseDateTime,
        artSendDate: updatedRow.simSendDate,
        comment: updatedRow.comment || ''
      };

      await updateArtDate(mappedUpdatedRow.artRequestID, mappedUpdatedRow);
      await getArtDatesData();
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        'An unexpected error occurred while updating the ART request.';
      setErrMsg(errorMessage);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentsClick = (row: any) => {
    setSelectedComments(row.commentsData || []);
    setSelectedRowForComments(row);
    setIsCommentsDialogOpen(true);
  };

  const handleCloseCommentsDialog = () => {
    setIsCommentsDialogOpen(false);
    setSelectedComments([]);
  };

  const getMonthNumber = (monthName: string): string => {
    const months = [
      '',
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    const monthIndex = months.indexOf(monthName);
    return monthIndex > 0 ? monthIndex.toString() : '1';
  };

  const handleAddComment = async (commentText: string) => {
    if (!selectedRowForComments || !commentText.trim()) {
      return;
    }

    setCommentLoading(true);
    try {
      // When adding a comment, include existing values to satisfy backend validation
      // but mark them as unchanged by using the exact same values
      const updatedRowData = {
        year: selectedRowForComments.year,
        month: getMonthNumber(selectedRowForComments.month),
        artOpenDateTime: selectedRowForComments.artOpenDateTime,
        artCloseDateTime: selectedRowForComments.artCloseDate,
        artSendDate: selectedRowForComments.artSendDate,
        comment: commentText,
        isCommentOnly: true // Flag to indicate this is a comment-only update
      };

      await updateArtDate(selectedRowForComments.id, updatedRowData);

      await getArtDatesData();
      handleCloseCommentsDialog();
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        'An unexpected error occurred while adding the comment.';
      setErrMsg(errorMessage);
      setShowModal(true);
    } finally {
      setCommentLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (showModal) {
    return (
      <>
        {showModal && (
          <AcDialog
            open={showModal}
            onClose={() => {
              setShowModal(false);
            }}
            data={{
              icon: 'icon-sad-emoji',
              title: 'Error',
              description: errMsg,
              actionLabel: 'Close',
              handleAction: () => {
                setShowModal(false);
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="art-requests-admin-title">
        <span className="title">{t('artRequestsAdmin.title')}</span>
      </div>
      <div className="art-requests-admin-table-cntr">
        <DataGridPro
          autoHeight
          columns={columns}
          disableColumnMenu
          disableRowSelectionOnClick
          rows={artDatesData || []}
          hideFooterRowCount
        />
      </div>

      <EditSimRequestDrawer
        show={isEditDrawerOpen}
        setToggle={setIsEditDrawerOpen}
        selectedRow={selectedRowForEdit}
        onSave={handleSaveEdit}
        adjacentRanges={undefined}
      />

      <CommentsDialog
        open={isCommentsDialogOpen}
        onClose={handleCloseCommentsDialog}
        comments={selectedComments}
        onAddComment={handleAddComment}
        isAdding={commentLoading}
      />
    </>
  );
}
